<div class="ai-batch" *ngIf="batch">

  <!-- Plan line: states the scope before any work is reported, so the accountant knows what
       they asked for. Figma: "I'll create accounts payable for 7 invoices: ..." -->
  <p class="ai-batch__plan" data-testid="ai-plan-line">
    <ng-container *ngIf="isRunning; else finishedPlan">
      Creating accounts payable drafts for {{ batch.total }}
      invoice{{ batch.total === 1 ? '' : 's' }}…
    </ng-container>
    <ng-template #finishedPlan>
      Finished {{ batch.total }} invoice{{ batch.total === 1 ? '' : 's' }}.
    </ng-template>
  </p>

  <!-- Real progress, not a spinner. A 20-invoice batch takes minutes; "x of y" is the only
       honest thing to show. -->
  <div class="ai-batch__progress" *ngIf="isRunning">
    <span class="ai-batch__progress-text">
      {{ completedCount }} of {{ batch.total }} done
    </span>
    <button
      type="button"
      class="ai-batch__cancel"
      data-testid="ai-batch-cancel"
      (click)="onCancel()">
      Stop
    </button>
  </div>

  <!-- One chip per invoice. -->
  <ul class="ai-batch__chips" role="list">
    <li
      *ngFor="let item of items; trackBy: trackById"
      class="ai-chip"
      [attr.data-testid]="'ai-step-chip'"
      [attr.data-invoice]="item.fileName">

      <!-- data-state lives on an inner element as well as the row, because the Playwright
           contract filters chips by a descendant [data-state] selector. -->
      <span class="ai-chip__state" [attr.data-state]="item.state">
        <i class="ai-chip__glyph" [ngClass]="iconFor(item.state)" aria-hidden="true"></i>
      </span>

      <span class="ai-chip__verb">{{ verbFor(item) }}</span>
      <span class="ai-chip__file" [title]="item.fileName">{{ item.fileName }}</span>

      <a
        *ngIf="linkFor(item) as href"
        class="ai-chip__link"
        [routerLink]="href"
        data-testid="ai-chip-link">
        {{ item.apNumber || 'View draft' }}
      </a>

      <span *ngIf="item.duplicateOf" class="ai-chip__note">
        matches {{ item.duplicateOf }}
      </span>

      <!-- A flag with no reason cannot be acted on, so the reason is always rendered when
           present. BatchStore refuses to record needs-attention or failed without one.

           One reason on the line, the rest behind a real control. The count used to be baked
           into the reason text as "(+3 more)", which looked clickable, was not, and referred to
           reasons the browser never received. -->
      <ng-container *ngIf="reasonsFor(item) as reasons">
        <span *ngIf="reasons.length" class="ai-chip__reason">{{ reasons[0] }}</span>

        <button
          *ngIf="hiddenReasonCount(item) > 0"
          type="button"
          class="ai-chip__more"
          data-testid="ai-chip-more"
          [attr.aria-expanded]="isExpanded(item)"
          (click)="toggleReasons(item)">
          {{ isExpanded(item) ? 'Show less' : '+' + hiddenReasonCount(item) + ' more' }}
        </button>

        <ul
          *ngIf="isExpanded(item)"
          class="ai-chip__reasons"
          data-testid="ai-chip-reasons"
          role="list">
          <li *ngFor="let reason of reasons.slice(1)">{{ reason }}</li>
        </ul>
      </ng-container>
    </li>
  </ul>

  <!-- Summary, once nothing is in flight. -->
  <div class="ai-batch__summary" *ngIf="isFinished" data-testid="ai-summary">
    <p class="ai-batch__summary-head">
      {{ countIn('created') + countIn('needs-attention') }} of {{ batch.total }}
      draft{{ batch.total === 1 ? '' : 's' }} created.
      <ng-container *ngIf="countIn('created') + countIn('needs-attention') > 0">
        Review and submit them in
        <a routerLink="/accounts-payable/list" data-testid="ai-summary-ap-link">Accounts Payable</a>.
      </ng-container>
    </p>

    <div
      *ngFor="let bucket of visibleBuckets; trackBy: trackByState"
      class="ai-batch__bucket"
      [attr.data-bucket]="bucket.state">

      <h4 class="ai-batch__bucket-head">
        {{ bucket.label }} ({{ countIn(bucket.state) }})
      </h4>

      <ul class="ai-batch__bucket-list" role="list">
        <li *ngFor="let item of itemsIn(bucket.state); trackBy: trackById" [attr.data-invoice]="item.fileName">
          <span class="ai-batch__bucket-file">{{ item.fileName }}</span>
          <a *ngIf="linkFor(item) as href" [routerLink]="href">{{ item.apNumber || 'View' }}</a>
          <span *ngIf="item.duplicateOf" class="ai-chip__note">matches {{ item.duplicateOf }}</span>

          <!-- The finished summary has room to be complete, so it lists every reason outright
               rather than hiding any behind a control. -->
          <ul class="ai-chip__reasons" *ngIf="reasonsFor(item).length" role="list">
            <li *ngFor="let reason of reasonsFor(item)">{{ reason }}</li>
          </ul>
        </li>
      </ul>
    </div>

    <p class="ai-batch__cancelled" *ngIf="batch.status === 'cancelled'">
      Stopped before the rest were started. Drafts already created are untouched — delete any you
      don't want in the Accounts Payable module.
    </p>
  </div>
</div>
